import React, { useState, useEffect } from 'react'
import { Info, ExternalLink } from 'lucide-react'
import api from '../services/api'

// Informational banner for settlement agreements that overlap the range a
// screen is filtered to. Context to have in front of you while reading the
// figures - not an error, not a warning. Blue rather than the amber the
// impersonation banner owns, and `role="status"` rather than `role="alert"`.
//
// Scoping is entirely server-side: `companies/settlements.py`'s `notices_for()`
// resolves the caller (impersonation first, then staff) and returns only what
// they may see, so a partner with no notices gets `[]` and there is nothing to
// filter or hide here. Don't add a client-side ownership check - it would be a
// second copy of an access rule that deliberately lives in one place.
//
// Omitting both `dateFrom` and `dateTo` is meaningful, not a missing prop: the
// backend treats an absent bound as an open end ("everything from here on"),
// so a screen that filters its figures by no date at all - Financial's
// cumulative balances, the partner's campaign list - passes no range and gets
// every notice that bears on those figures. Passing a made-up window there
// would hide the carried-over agreement the balance needs explaining by.
export default function SettlementNoticeBanner({ dateFrom, dateTo }) {
  const [notices, setNotices] = useState([])

  useEffect(() => {
    let cancelled = false
    const params = {}
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo

    api.get('/settlements/notices', { params })
      .then(({ data }) => {
        if (!cancelled) setNotices(Array.isArray(data) ? data : [])
      })
      // Silent on purpose. This is a strip of context above screens whose
      // own data loaded fine; a toast or an error state here would turn a
      // missing footnote into a visible failure of the whole page.
      .catch(() => {
        if (!cancelled) setNotices([])
      })

    return () => { cancelled = true }
  }, [dateFrom, dateTo])

  return <SettlementNoticeList notices={notices} />
}

// The rendering half on its own, for a screen that already has the notices
// in hand - the partner dashboard gets them inside /partner/dashboard, cut
// to the same range as every other block, and fetching them a second time
// here would be a second answer to the same question.
export function SettlementNoticeList({ notices }) {
  if (!notices || notices.length === 0) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {notices.map(n => <NoticeRow key={n.id} notice={n} />)}
    </div>
  )
}

function NoticeRow({ notice }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 14px',
        background: 'var(--blue-bg)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--blue)',
        borderRadius: 'var(--radius)',
      }}
    >
      <Info size={16} style={{ color: 'var(--blue)', flexShrink: 0, marginTop: 1 }} />
      <span style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
        <NoticeMessage notice={notice} />
      </span>
    </div>
  )
}

// The message is the backend's to word, so it is rendered whole and never
// rebuilt from `amount`/`period_start`/`affiliate_name`. The only thing done
// to it is splitting on `document_link_text` - an exact substring of `message`
// - to turn that run of words into the link.
function NoticeMessage({ notice }) {
  const { message, document_link_text: linkText, document_url: url } = notice

  // The serializer ties these together (a notice with no document gets both
  // an empty link text and a null url), but they are checked separately so a
  // link text that doesn't actually occur in the message degrades the same
  // quiet way rather than rendering an anchor around nothing.
  const at = linkText && url ? message.indexOf(linkText) : -1
  if (at === -1) return message

  return (
    <>
      {message.slice(0, at)}
      <a
        href={url}
        // New tab, and no `download`. The date filter these screens are read
        // through lives in component state, not the URL, so navigating away
        // in place discards the filter the user just set. The backend also
        // sends `Content-Disposition: inline` precisely so the PDF opens next
        // to the figures it explains; `download` would override that and
        // force a save, and is silently ignored cross-origin besides. Opening
        // a tab leaves saving available from the viewer.
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--blue)',
          textDecoration: 'underline',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {linkText}
        <ExternalLink size={12} style={{ marginLeft: 4, verticalAlign: '-1px' }} />
      </a>
      {message.slice(at + linkText.length)}
    </>
  )
}
